import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import { useQueryClient } from '@tanstack/react-query'
import type { Basket } from '@reward-platform/order-schemas/basket'
import type { PlatformThemeId } from '@reward-platform/platform-config'
import { useAuthContext } from '~/context/auth'
import { useFeatureFlag } from '~/context/featureFlag'
import { usePlatformConfig } from '~/context/platform'
import { clearBasketItemsOnPlatformSwitch } from '~/services/basketService'
import { BASKET_KEY } from '~/hooks/useBasket/useBasket'
import { isClient } from '~/utils/envChecks'
import { getBasketItemTypesToClearOnPlatformSwitch } from '~/utils/basketPlatformSwitch'

export const CLEAR_BASKET_ITEMS_ON_PLATFORM_SWITCH_FLAG =
  'should-clear-basket-items-on-platform-switch'

const BASKET_PAGE_PATH = '/orders/payment-options'
const SESSION_STORAGE_KEY_PREFIX = 'clear-basket-items-on-platform-switch-complete'

type CleanupStatus = 'idle' | 'checking' | 'complete' | 'failed'

type CleanupState = {
  scope: string
  status: CleanupStatus
  basketPagePlatformSwitchCleanupFailed: boolean
}

type BasketPlatformSwitchCleanupContextValue = {
  isChecking: boolean
  basketPagePlatformSwitchCleanupFailed: boolean
}

const DEFAULT_CONTEXT_VALUE: BasketPlatformSwitchCleanupContextValue = {
  isChecking: false,
  basketPagePlatformSwitchCleanupFailed: false,
}

const BasketPlatformSwitchCleanupContext =
  createContext<BasketPlatformSwitchCleanupContextValue>(DEFAULT_CONTEXT_VALUE)

const getSessionStorageKey = (theme: string, userId: string) =>
  `${SESSION_STORAGE_KEY_PREFIX}:${theme}:${userId}`

const readSessionCheckCompleted = (key: string): boolean => {
  if (isClient()) {
    try {
      return window.sessionStorage.getItem(key) === 'true'
    } catch {
      return false
    }
  }

  return false
}

const setSessionCheckCompleted = (key: string) => {
  if (isClient()) {
    try {
      window.sessionStorage.setItem(key, 'true')
    } catch {
      // If sessionStorage is unavailable, the next page visit can safely retry.
    }
  }
}

const isBasketPagePath = (pathname: string, asPath: string) =>
  pathname === BASKET_PAGE_PATH || asPath.split('?')[0].endsWith(BASKET_PAGE_PATH)

const getBasketPageItemScope = (basket: Basket | undefined, theme: PlatformThemeId) => {
  const basketItemIds: string[] = []
  const itemTypesToClear = getBasketItemTypesToClearOnPlatformSwitch(theme)

  itemTypesToClear.forEach((itemType) => {
    basket?.items[itemType].forEach(({ id }) => {
      basketItemIds.push(id ?? '')
    })
  })

  basketItemIds.sort((firstItemId, secondItemId) => firstItemId.localeCompare(secondItemId))

  return basketItemIds.join('|')
}

const getCleanupScope = ({
  basketPageItemScope,
  isBasketPage,
  routeScope,
  sessionStorageKey,
}: {
  basketPageItemScope: string
  isBasketPage: boolean
  routeScope: string
  sessionStorageKey: string
}) =>
  [
    isBasketPage ? 'basket' : 'session',
    sessionStorageKey,
    routeScope,
    isBasketPage ? basketPageItemScope : '',
  ].join(':')

export const useClearBasketItemsOnPlatformSwitchContext =
  (): BasketPlatformSwitchCleanupContextValue => useContext(BasketPlatformSwitchCleanupContext)

export const ClearBasketItemsOnPlatformSwitchProvider = ({ children }: React.PropsWithChildren) => {
  const router = useRouter()
  const queryClient = useQueryClient()
  const { isAuthenticated, userId } = useAuthContext()
  const { theme } = usePlatformConfig()
  const isFeatureEnabled = useFeatureFlag(CLEAR_BASKET_ITEMS_ON_PLATFORM_SWITCH_FLAG)
  const [cleanupState, setCleanupState] = useState<CleanupState>({
    scope: '',
    status: 'idle',
    basketPagePlatformSwitchCleanupFailed: false,
  })
  const [basketPageItemScope, setBasketPageItemScope] = useState(() =>
    getBasketPageItemScope(queryClient.getQueryData<Basket>([BASKET_KEY]), theme)
  )

  const isBasketPage = isBasketPagePath(router.pathname, router.asPath)
  const sessionStorageKey = userId ? getSessionStorageKey(theme, userId) : ''
  const sessionCheckCompleted = sessionStorageKey
    ? readSessionCheckCompleted(sessionStorageKey)
    : false

  const shouldRunCleanup =
    isAuthenticated &&
    Boolean(userId) &&
    isFeatureEnabled &&
    (isBasketPage || !sessionCheckCompleted)

  const routeScope = router.asPath || router.pathname
  const cleanupScope = shouldRunCleanup
    ? getCleanupScope({ basketPageItemScope, isBasketPage, routeScope, sessionStorageKey })
    : ''

  const hasCurrentCleanupSettled =
    cleanupState.scope === cleanupScope &&
    (cleanupState.status === 'complete' || cleanupState.status === 'failed')

  const isChecking = shouldRunCleanup && !hasCurrentCleanupSettled

  useEffect(() => {
    if (!isBasketPage) {
      return undefined
    }

    let isSubscribed = true
    let isUpdateQueued = false

    const updateBasketPageItemScope = () => {
      if (!isSubscribed) {
        return
      }

      const nextBasketPageItemScope = getBasketPageItemScope(
        queryClient.getQueryData<Basket>([BASKET_KEY]),
        theme
      )

      setBasketPageItemScope((currentBasketPageItemScope) =>
        currentBasketPageItemScope === nextBasketPageItemScope
          ? currentBasketPageItemScope
          : nextBasketPageItemScope
      )
    }

    const queueBasketPageItemScopeUpdate = () => {
      if (isUpdateQueued) {
        return
      }

      isUpdateQueued = true

      queueMicrotask(() => {
        isUpdateQueued = false
        updateBasketPageItemScope()
      })
    }

    updateBasketPageItemScope()

    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event.query.queryKey[0] === BASKET_KEY) {
        queueBasketPageItemScopeUpdate()
      }
    })

    return () => {
      isSubscribed = false
      unsubscribe()
    }
  }, [isBasketPage, queryClient, theme])

  useEffect(() => {
    if (!shouldRunCleanup || !sessionStorageKey || !cleanupScope || hasCurrentCleanupSettled) {
      return undefined
    }

    let isMounted = true

    setCleanupState({
      scope: cleanupScope,
      status: 'checking',
      basketPagePlatformSwitchCleanupFailed: false,
    })

    clearBasketItemsOnPlatformSwitch(theme)
      .then(({ basket }) => {
        queryClient.setQueryData([BASKET_KEY], basket)

        if (!isBasketPage) {
          setSessionCheckCompleted(sessionStorageKey)
        }

        if (isMounted) {
          const completedCleanupScope = isBasketPage
            ? getCleanupScope({
                basketPageItemScope: getBasketPageItemScope(basket, theme),
                isBasketPage,
                routeScope,
                sessionStorageKey,
              })
            : cleanupScope

          setCleanupState({
            scope: completedCleanupScope,
            status: 'complete',
            basketPagePlatformSwitchCleanupFailed: false,
          })
        }
      })
      .catch(() => {
        if (isMounted) {
          setCleanupState({
            scope: cleanupScope,
            status: 'failed',
            basketPagePlatformSwitchCleanupFailed: isBasketPage,
          })
        }
      })

    return () => {
      isMounted = false
    }
  }, [
    cleanupScope,
    hasCurrentCleanupSettled,
    isBasketPage,
    queryClient,
    routeScope,
    sessionStorageKey,
    shouldRunCleanup,
    theme,
  ])

  const contextValue = useMemo<BasketPlatformSwitchCleanupContextValue>(
    () => ({
      isChecking,
      basketPagePlatformSwitchCleanupFailed:
        cleanupState.scope === cleanupScope && cleanupState.basketPagePlatformSwitchCleanupFailed,
    }),
    [
      cleanupScope,
      cleanupState.basketPagePlatformSwitchCleanupFailed,
      cleanupState.scope,
      isChecking,
    ]
  )

  if (isChecking) {
    return (
      <BasketPlatformSwitchCleanupContext.Provider value={contextValue}>
        {null}
      </BasketPlatformSwitchCleanupContext.Provider>
    )
  }

  return (
    <BasketPlatformSwitchCleanupContext.Provider value={contextValue}>
      {children}
    </BasketPlatformSwitchCleanupContext.Provider>
  )
}
